import { Injectable, Injector } from '@angular/core'
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpEvent,
  HttpClient,
  HttpHeaders,
} from '@angular/common/http'
import { Router } from '@angular/router'
import { Observable, throwError, BehaviorSubject } from 'rxjs'
import { switchMap, take, catchError, filter } from 'rxjs/operators'
import { BasicAuthService } from '../../services/basic-auth.service'
import { environment } from '../../../environments/environment'

@Injectable()
export class SpasInterceptor implements HttpInterceptor {
  private readonly authService: BasicAuthService
  private readonly http: HttpClient

  // null = refresh in-progress, string = new token, HttpErrorResponse = refresh failed
  private isRefreshing: boolean = false
  private refreshDone$: BehaviorSubject<string | null | HttpErrorResponse> = new BehaviorSubject<string | null | HttpErrorResponse>(null)

  private readonly refreshUrl = environment.apiUrl + '/auth/refresh'

  public constructor(private injector: Injector, private router: Router) {
    this.authService = this.injector.get(BasicAuthService)
    this.http = this.injector.get(HttpClient)
  }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Never intercept the refresh call itself to avoid infinite loops
    if (request.url === this.refreshUrl) {
      return next.handle(request)
    }

    return this.authService.isAuthenticated().pipe(
      take(1),
      switchMap((authenticated: boolean): Observable<HttpEvent<any>> => {
        const token: string | null = this.authService.getToken()

        // Page reload: session cookie alive but JWT lost from memory — refresh first
        if (authenticated && !token) {
          return this.doRefresh(request, next)
        }

        return next.handle(this.attachToken(request, token)).pipe(
          catchError((error: HttpErrorResponse): Observable<HttpEvent<any>> => {
            if (error.status === 401) {
              if (authenticated) {
                return this.doRefresh(request, next)
              }

              this.redirectToLogin()
            }
            return throwError((): HttpErrorResponse => error)
          }),
        )
      }),
    )
  }

  private attachToken(request: HttpRequest<any>, token?: string | null): HttpRequest<any> {
    let headers: HttpHeaders = request.headers
      .set('Cache-Control', 'no-cache')
      .set('Pragma', 'no-cache')
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`)
    }
    return request.clone({ headers, withCredentials: true })
  }

  private redirectToLogin(): void {
    this.authService.logout(true).then()

    if (this.authService.hasADIntegration()) {
      this.router.navigate(['windows/login']).then()
    } else {
      this.router.navigate(['auth/login']).then()
    }
  }

  /**
   * Single refresh gate used for both page-reload (token=null) and mid-session 401.
   * Only one HTTP call is ever made — concurrent requests wait on refreshDone$.
   */
  private doRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.refreshDone$.pipe(
        filter((value: string | HttpErrorResponse): boolean => value !== null),
        take(1),
        switchMap((value: string | HttpErrorResponse): Observable<HttpEvent<any>> => {
          if (value instanceof HttpErrorResponse) {
            return throwError((): HttpErrorResponse => value)
          }
          return next.handle(this.attachToken(request, value as string))
        }),
      )
    }

    this.isRefreshing = true
    this.refreshDone$.next(null)

    return this.http
      .post<{
        jwt_token: string
        expires_in: number
      }>(this.refreshUrl, {}, { withCredentials: true })
      .pipe(
        switchMap(
          (result: { jwt_token: string; expires_in: number }): Observable<HttpEvent<any>> => {
            const newToken: string | null = result?.jwt_token ?? null
            if (newToken) {
              this.authService.setToken(newToken)
            }
            this.isRefreshing = false
            // Broadcast the token so all queued requests can proceed
            this.refreshDone$.next(newToken)
            return next.handle(this.attachToken(request, newToken))
          },
        ),
        catchError((err: HttpErrorResponse | Error) => {
          this.isRefreshing = false
          const httpErr: HttpErrorResponse = err instanceof HttpErrorResponse
            ? err
            : new HttpErrorResponse({ error: err, status: 0, statusText: 'Refresh failed' })
          // Broadcast the error so all queued requests unblock and receive the error
          this.refreshDone$.next(httpErr)
          this.redirectToLogin()
          return throwError((): HttpErrorResponse => httpErr)
        }),
      )
  }
}
